import ImageUrlBuilder from '@sanity/image-url'
import classNames from 'classnames'
import Image from 'next/image'
import { useRef } from 'react'
import Slider from 'react-slick'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import urls from '../../../modules/urls'
import { getDday } from '../../../modules/utils'
import { D_DAY_TYPE, DdayCounter } from '../../common/DdayCounter'
import { convertToEmbedUrl } from '../../sanity/util'
import {
  IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_LEFT,
  IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_RIGHT,
} from '../constants/home.constants'
import LandingNav from '../navigation/LandingNav'
import Footer from '../section/footer'
import PricingDesktopBox from './PricingDesktopBox'

export const ImageBox = ({ item, index, isPricingPage }) => {
  if (!item) return
  return (
    <section id={index} className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <div
        className={classNames('mx-auto max-w-[1040px]', {
          'max-w-[1180px]': isPricingPage,
          'max-w-[1040px]': !isPricingPage,
        })}
      >
        <Image
          priority={true}
          alt={item?.image?.alt || ''}
          className="mx-auto w-full delay-[initial] "
          src={urlFor(item?.image).url()}
          style={{
            width: '100%',
            height: 'auto',
          }}
          width={1040}
          height={1040}
          quality={100}
        />
      </div>
    </section>
  )
}

export const YoutubeBox = ({ item, index }) => {
  if (!item) return
  return (
    <section id={index} className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <div className="mx-auto max-w-[1040px]">
        <div className="mx-auto aspect-[16/9]" style={{ maxWidth: `${item?.width}px` }}>
          <iframe
            width="100%"
            height="100%"
            className="aspect-[16/9]"
            src={convertToEmbedUrl(item?.youtubeLink)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    </section>
  )
}

export const VideoBox = ({ item, index }) => {
  if (!item) return
  return (
    <section id={index} className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <div className="mx-auto max-w-[1040px]">
        <video autoPlay loop muted={true} playsInline preload="none" className="mx-auto w-full">
          <source src={item?.video_link} type="video/mp4" />
        </video>
      </div>
    </section>
  )
}
export function urlFor(source: string | any) {
  const builder = ImageUrlBuilder(client)
  return builder.image(source)
}

const ButtonBox = ({ item, index }) => {
  const handleClick = () => {
    window.open(item?.link, '_blank')
  }

  return (
    <section id={index} className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <Image
        alt={item?.image?.alt || ''}
        className="mx-auto cursor-pointer delay-[initial]"
        onClick={handleClick}
        priority={true}
        src={urlFor(item?.image).url()}
        style={{
          width: `${item?.width}px`,
          height: 'auto',
        }}
        width={item?.width}
        height={item?.width}
        quality={100}
      />
    </section>
  )
}

const LOCATION = {
  RIGHT: 'right',
  CENTER: 'center',
  LEFT: 'left',
}
const FloatingButtonBox = ({ item }) => {
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()

  const getFullUrl = useReturnFullUrl()
  const handleClick = () => {
    if (item?.link.includes('/pricing')) {
      router.push(currentUser ? urls.student.newPortal.pricing.index : urls.student.landing.pricing, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(`${urls.student.portal.pricing.index}?app=true`),
        },
      })
    } else {
      if (item?.link.includes('https://')) {
        window.open(item?.link, '_blank')
      } else {
        router.push(item?.link)
      }
    }
  }

  return (
    <section
      className={classNames(
        'fixed z-[10]',
        item?.location === LOCATION.RIGHT && 'bottom-[16px] right-[16px]',
        item?.location === LOCATION.LEFT && 'bottom-[16px] left-[16px]',
        item?.location === LOCATION.CENTER && 'bottom-[16px] left-1/2 -translate-x-1/2 transform'
      )}
      style={{ width: `${item?.width}px` }}
    >
      <Image
        alt={item?.image?.alt || ''}
        className="cursor-pointer delay-[initial]"
        onClick={handleClick}
        src={urlFor(item?.image).url()}
        style={{
          width: `${item?.width}px`,
          borderRadius: `${item?.radius}px`,
          height: 'auto',
        }}
        width={item?.width}
        height={item?.width}
        quality={100}
      />
    </section>
  )
}
const DdayBox = ({ item, index }) => {
  const { t } = useTranslation()
  const dueDate = Date.UTC(item?.year, item?.month - 1, item?.dates, item?.hours)
  const dday = getDday(dueDate)
  const date = new Date(dueDate)

  return (
    <section id={index} className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <div className="mx-auto">
        <DdayCounter
          initialDay={new Date()}
          targetDay={date}
          types={dday < 1 ? [D_DAY_TYPE.HOUR, D_DAY_TYPE.MIN, D_DAY_TYPE.SEC] : [D_DAY_TYPE.DAY]}
          classNameTotalContainer="flex flex-row justify-center relative"
          classNameDigitContainer="flex flex-row justify-center gap-x-[4px]"
          classNameDigit="w-[48px] h-[68px] leading-[68px] sm:w-[60px] sm:h-[84px] sm:leading-[84px] text-[52px] sm:text-[60px] text-gray-900 font-bold rounded-[3.2px] bg-white text-center drop-shadow-[5px_5px_5px_rgba(67,86,100,0.25)]"
          seperator={
            <div className="mx-[10px] flex flex-col justify-center gap-y-[8px]">
              <div className="h-[6px] w-[6px] rounded-full bg-black" />
              <div className="h-[6px] w-[6px] rounded-full bg-black" />
            </div>
          }
          typeLabel={null}
          classNameTypeLabel="font-medium text-center text-[14px] leading-[21px] mt-[4px] text-white"
          text={null}
        />
      </div>
    </section>
  )
}

const ScrollTab = ({ item }) => {
  const handleClickTab = (sectionId) => {
    const element = document?.getElementById(sectionId)

    if (element) {
      const topPos = element.offsetTop

      window.scrollTo({
        top: topPos,
        behavior: 'smooth',
      })
    }
  }

  return (
    <section className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <div className="mx-auto max-w-[1040px]">
        <div className="flex flex-row justify-center text-center">
          {item?.tabList.map((tab, i) => {
            return (
              <div
                className="Body1-16Md w-full cursor-pointer px-[13px]"
                key={i}
                style={{
                  backgroundColor: tab?.tabBackgroundColor.hex,
                  paddingTop: `${item?.paddingY}px` || '8px',
                  paddingBottom: `${item?.paddingY}px` || '8px',
                }}
                onClick={() => handleClickTab(tab?.tabScrollId)}
              >
                <div style={{ color: tab?.tabTitleColor.hex }}>{tab?.tabTitle}</div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

const CarouselBox = ({ item, index }) => {
  const sliderRef = useRef<Slider>(null)

  const isImageCarousel = item?.carouselType == 'image'
  const carouselList = isImageCarousel ? item?.imageList : item?.videoLinkList
  return (
    <section className="w-full" style={{ backgroundColor: item?.backgroundColor?.hex }}>
      <div className="relative mx-auto w-full max-w-[1040px]">
        <Slider
          ref={sliderRef}
          className="slider slider-gradient"
          slidesToShow={1}
          prevArrow={<></>}
          nextArrow={<></>}
          autoplay={item?.autoplay}
          autoplaySpeed={item?.autoplaySpeed}
          infinite={Boolean(item?.infinite)}
          centerMode={true}
          variableWidth={true}
          draggable={true}
        >
          {carouselList.map((item, i) => (
            <div key={item?.id} className="w-full px-[12px]">
              <div className="flex w-full items-center justify-center">
                {isImageCarousel ? (
                  <Image
                    priority={true}
                    alt={item?.image?.alt || ''}
                    className="mx-auto delay-[initial]"
                    src={urlFor(item?.asset).url()}
                    style={{
                      width: `${item?.width}px`,
                      height: 'auto',
                    }}
                    width={item?.width}
                    height={200}
                    quality={100}
                  />
                ) : (
                  <video
                    autoPlay
                    loop
                    muted={true}
                    playsInline
                    preload="none"
                    className="mx-auto w-full"
                    style={{
                      width: `${item?.width}px`,
                      height: `${item?.height}px`,
                    }}
                  >
                    <source src={item?.link} type="video/mp4" />
                  </video>
                )}
              </div>
            </div>
          ))}
        </Slider>

        <div className="mt-[32px] flex w-full items-center justify-center space-x-[24px]">
          <button onClick={() => sliderRef?.current?.slickPrev()}>
            <img src={IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_LEFT} alt="left-button" />
          </button>
          <button onClick={() => sliderRef?.current?.slickNext()}>
            <img src={IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_RIGHT} alt="right-button" />
          </button>
        </div>
      </div>
    </section>
  )
}

const TYPE = {
  IMAGE_BOX: 'eventSectionImageBox',
  VIDEO_BOX: 'eventSectionVideoBox',
  BUTTON_BOX: 'eventSectionButtonBox',
  FLOATING_BUTTON_BOX: 'eventSectionFloatingButtonBox',
  DDAY_BOX: 'eventSectionDdayBox',
  SCROLL_TAB: 'eventSectionScrollTab',
  YOUTUBE_BOX: 'eventSectionYoutubeBox',
  CAROUSEL_BOX: 'eventSectionCarouselBox',
  PRICING_BOX: 'eventSectionPricingBox',
}

export const PlusDesktopSectionItem = ({ item, index, isPricingPage = false }) => {
  if (item?._type === TYPE.IMAGE_BOX) return <ImageBox item={item} index={index} isPricingPage={isPricingPage} />
  if (item?._type === TYPE.VIDEO_BOX) return <VideoBox item={item} index={index} />
  if (item?._type === TYPE.BUTTON_BOX) return <ButtonBox item={item} index={index} />
  if (item?._type === TYPE.FLOATING_BUTTON_BOX) return <FloatingButtonBox item={item} />
  if (item?._type === TYPE.DDAY_BOX) return <DdayBox item={item} index={index} />
  if (item?._type === TYPE.SCROLL_TAB) return <ScrollTab item={item} />
  if (item?._type === TYPE.YOUTUBE_BOX) return <YoutubeBox item={item} index={index} />
  if (item?._type === TYPE.CAROUSEL_BOX) return <CarouselBox item={item} index={index} />
  if (item?._type === TYPE.PRICING_BOX) return <PricingDesktopBox item={item} index={index} />
}

const PlusDesktopSections = ({ value }) => {
  return (
    <div className="relative">
      <LandingNav />

      {value.map((sectionItem, index) => (
        <PlusDesktopSectionItem item={sectionItem} key={sectionItem?._id} index={index} />
      ))}
      <Footer />
    </div>
  )
}

export default PlusDesktopSections
